import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { CustomButton } from "../../../atoms/CustomButton";
import { StatusBadge } from "../../../atoms/StatusBadge";
import NoTranslate from "../../../atoms/NoTranslate";
import { useGetGameHistory } from "../../../../hooks/entity";

const PREVIEW_COUNT = 5;

interface GamingHistorySectionProps {
  customerIdentification: string;
  entityIdentification: string;
  userIdentification: string;
  enabled: boolean;
}

const GamingHistorySection: React.FC<GamingHistorySectionProps> = ({
  customerIdentification,
  entityIdentification,
  userIdentification,
  enabled,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const { data } = useGetGameHistory(
    customerIdentification,
    entityIdentification,
    userIdentification,
    enabled
  );

  const records = useMemo(
    () =>
      [...(data || [])].sort(
        (a, b) =>
          new Date(b.played_at).getTime() - new Date(a.played_at).getTime()
      ),
    [data]
  );

  if (!enabled || records.length === 0) return null;

  const pendingCount = records.filter(
    (r) => r.result === "WIN" && !r.prize_applied
  ).length;
  const displayed = showAll ? records : records.slice(0, PREVIEW_COUNT);

  return (
    <div className="self-stretch bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-100 pb-3">
        <h2 className="text-sm font-semibold text-gray-900">
          {t("dashboard.entities.viewEntityUser.gaming.title")}
        </h2>
        {pendingCount > 0 && (
          <StatusBadge color="warning" dot>
            {t("dashboard.entities.viewEntityUser.gaming.pendingCount", {
              count: pendingCount,
            })}
          </StatusBadge>
        )}
      </div>

      <p className="mt-3 text-xs text-gray-500">
        {t("dashboard.entities.viewEntityUser.gaming.hint")}
      </p>

      <ul className="mt-2 divide-y divide-gray-100">
        {displayed.map((record) => {
          const isWin = record.result === "WIN";
          const prize = record.prize_details;
          return (
            <li
              key={`${record.segment_id}_${record.played_at}`}
              className="flex flex-wrap items-start gap-x-3 gap-y-1 py-3"
            >
              <div className="min-w-0 flex-1 basis-48">
                {isWin ? (
                  <p className="break-words text-sm font-medium text-gray-900">
                    <NoTranslate>{prize?.display_text}</NoTranslate>
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">
                    {t("dashboard.entities.viewEntityUser.gaming.lost")}
                  </p>
                )}
                {isWin && prize?.description && (
                  <p className="break-words text-xs text-gray-500">
                    <NoTranslate>{prize.description}</NoTranslate>
                  </p>
                )}
                <p className="mt-0.5 text-xs text-gray-500">
                  {moment(record.played_at).format("lll")}
                </p>
              </div>
              {isWin && (
                <StatusBadge
                  color={record.prize_applied ? "success" : "warning"}
                  dot
                >
                  {record.prize_applied
                    ? t("dashboard.entities.viewEntityUser.gaming.handedOver")
                    : t("dashboard.entities.viewEntityUser.gaming.toHandOver")}
                </StatusBadge>
              )}
            </li>
          );
        })}
      </ul>

      {records.length > PREVIEW_COUNT && (
        <div className="mt-4 flex justify-center">
          <CustomButton
            variant="secondary"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            title={
              showAll
                ? t("dashboard.entities.viewEntityUser.viewLess")
                : t("dashboard.entities.viewEntityUser.viewMore")
            }
          />
        </div>
      )}
    </div>
  );
};

export default GamingHistorySection;
